import { RecentlyViewed } from '@theme/recently-viewed-products';

export class SccRecentlyViewed extends HTMLElement {
  #abortController;
  #intersectionObserver;

  connectedCallback() {
    if ('IntersectionObserver' in window) {
      this.#intersectionObserver = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.#intersectionObserver?.disconnect();
          void this.load();
        },
        { rootMargin: '0px 0px 600px' }
      );
      this.#intersectionObserver.observe(this);
      return;
    }

    void this.load();
  }

  disconnectedCallback() {
    this.#intersectionObserver?.disconnect();
    this.#abortController?.abort();
  }

  async load() {
    if (this.dataset.loaded === 'true' || this.dataset.loading === 'true') return;

    const currentProductId = String(this.dataset.currentProductId || '');
    const productIds = RecentlyViewed.getProducts()
      .map((productId) => String(productId || '').trim())
      .filter((productId) => /^\d+$/.test(productId))
      .filter((productId) => productId !== currentProductId)
      .slice(0, 4);

    if (productIds.length === 0 || !this.dataset.searchUrl || !this.dataset.sectionId) return;

    this.dataset.loading = 'true';
    this.setAttribute('aria-busy', 'true');
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const url = new URL(this.dataset.searchUrl, window.location.origin);
      url.searchParams.set('q', productIds.map((productId) => `id:${productId}`).join(' OR '));
      url.searchParams.set('type', 'product');
      url.searchParams.set('section_id', this.dataset.sectionId);

      const response = await fetch(url, {
        signal: this.#abortController.signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!response.ok) return;

      const markup = await response.text();
      const documentFragment = new DOMParser().parseFromString(markup, 'text/html');
      const results = documentFragment.querySelector('[data-scc-recently-viewed-results]');
      if (!results?.querySelector('[data-product-id]') || this.#abortController.signal.aborted) return;

      this.replaceChildren(results.cloneNode(true));
      this.dataset.loaded = 'true';
      this.dataset.state = 'loaded';
      this.removeAttribute('aria-hidden');
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      // Recently viewed products are supplementary and fail silently.
    } finally {
      delete this.dataset.loading;
      this.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('scc-recently-viewed')) {
  customElements.define('scc-recently-viewed', SccRecentlyViewed);
}
